import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import './ChatModal.css'
import {
  FaFileAlt,
  FaFileExcel,
  FaFilePdf,
  FaFileImage,
  FaUser,
  FaPaperclip,
  FaCross,
  FaTimes,
} from 'react-icons/fa'
import { useAuth } from '../../../modules/auth'
import Pusher from 'pusher-js'
import moment from 'moment'

interface ChatModalProps {
  isOpen: boolean
  onClose: () => void
  message: string
  claimDetails: any
  onRefetchDetailsClaim?: () => void
}

interface ChatMessage {
  remark: string
  documentName: string[]
  role: string
  createdBy: string
  createdAt: string
  updatedBy?: string | null // Allow updatedBy to be null
  updatedAt?: string | null // Allow updatedAt to be null
  chatIsOpen: boolean
  count: number
  isPending?: boolean
  tempId?: string
  avatarUrl?: string
}

const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose, message, claimDetails, onRefetchDetailsClaim }) => {
  const user = useAuth()
  const { auth } = useAuth()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const messagesEndRef = useRef<HTMLDivElement | null>(null) // Ref for scrolling

  // Declare the pusher instance
  let pusher: Pusher | null = null

  // Check if claimDetails is defined and has the expected structure
  const claim = claimDetails ? claimDetails : null // Adjust based on your actual structure
  console.log('CONSOLE LOG : ', claim)

  const fetchMessages = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ClaimChatProvider`,
        {
          params: {
            claimNumber: claim?.claimNumber || claimDetails.claimDetails.claimNumber,
            providerCode: claim?.providerCode || claimDetails.claimDetails.providerCode,
            role: 'PROVIDER',
            updatedBy: user.currentUser?.email,
          },
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      if (response.data.isSuccess) {
        const transformedMessages = response.data.data.map((msg: any, index: number) => ({
          remark: msg.remark,
          documentName: msg.documentName || [], // Keep the full array of document names
          role: msg.role,
          createdBy: msg.createdBy,
          createdAt: msg.createdAt,
          updatedBy: msg.updatedBy,
          updatedAt: msg.updatedAt,
          chatIsOpen: msg.chatIsOpen,
          count: msg.count,
          isPending: false, // Assuming messages fetched are not pending
        }))
        setMessages(transformedMessages)
        if (onRefetchDetailsClaim) {
          onRefetchDetailsClaim()
        }
      } else {
        toast.error('Failed to fetch messages')
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
      toast.error('Error fetching messages')
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchMessages()
    }
  }, [isOpen, claim])

  // Scroll to the latest message when the chat is opened
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isOpen, messages])

  // Pusher setup
  useEffect(() => {
    pusher = new Pusher(`${process.env.REACT_APP_API_PUSHER_KEY!}`, {
      cluster: process.env.REACT_APP_API_PUSHER_CLUSTER! || 'ap1',
      forceTLS: true,
    })

    const providerCode = claim?.providerCode
    const claimNumber = claim?.claimNumber
    const channel = `MessageChannel-${providerCode}-${claimNumber}`
    const event = `Message-${providerCode}-${claimNumber}`

    // Subscribe to the channel
    const channelInstance = pusher.subscribe(channel)
    channelInstance.bind(event, (data) => {
      const newMessage = data.message
      const transformedMessage = transformPusherMessage(newMessage)

      // Check if the message was sent by the current user
      if (transformedMessage && transformedMessage.createdBy !== user.currentUser?.email) {
        setMessages((prev) => [...prev, transformedMessage])
      }
    })

    return () => {
      pusher?.unsubscribe(channel)
    }
  }, [claim])

  const handleFileUpload = (file: File) => {
    // Check file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PNG, JPG, JPEG and PDF files are allowed')
      return
    }

    // Check if already uploading
    if (isUploading) {
      toast.error('Please wait for the current upload to complete')
      return
    }

    setIsUploading(true)
    setLoading(true) // Also set loading to true to disable send button

    // Simulate upload delay and add file
    setTimeout(() => {
      setUploadedFiles((prevFiles) => [...prevFiles, file])
      setIsUploading(false)
      setLoading(false) // Re-enable send button after upload
    }, 1000)
  }

  const handleRemoveFile = (fileToRemove: File) => {
    // Remove the selected file from the uploaded files state
    setUploadedFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove))
  }

  const handleSendMessage = async () => {
    if (isUploading) {
      toast.error('Please wait for file upload to complete')
      return
    }

    // Validate that either there's a message or files to send
    if (!inputValue.trim() && uploadedFiles.length === 0) {
      toast.error('Please type a message or upload a file before sending.')
      return
    }

    console.log('Input Value : ', inputValue)

    const formData = new FormData()
    formData.append(
      'ClaimNumber',
      claim?.claimNumber || claimDetails.claimDetails.claimNumber || ''
    )
    formData.append(
      'ProviderCode',
      claim?.providerCode || claimDetails.claimDetails.providerCode || ''
    )
    formData.append(
      'Remark',
      inputValue.trim() || (uploadedFiles.length > 0 ? 'File uploaded' : '')
    ) // Only use "File uploaded" if there are files
    formData.append('DocumentName', '') // Set to empty as per new API
    formData.append('Role', 'PROVIDER')
    formData.append('UpdatedBy', user.currentUser?.email || 'SYSTEM')

    // Append uploaded files to the form data
    uploadedFiles.forEach((file) => {
      // Ensure each file is appended correctly
      formData.append('Attachment', file)
    })

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ClaimChatProvider`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      if (response.data.isSuccess) {
        // Handle successful message send
        const messageData: ChatMessage = {
          remark: inputValue.trim() || (uploadedFiles.length > 0 ? 'File uploaded' : ''), // Only use "File uploaded" if there are files
          documentName: uploadedFiles.map((file) => file.name), // List of uploaded file names
          role: 'PROVIDER',
          createdBy: user.currentUser?.email || 'SYSTEM',
          createdAt: new Date().toISOString(),
          updatedBy: user.currentUser?.email || 'SYSTEM',
          updatedAt: new Date().toISOString(),
          chatIsOpen: true,
          count: 0,
          isPending: false,
          tempId: Date.now().toString(),
        }

        // Only add the message if the remark is not empty
        if (messageData.remark) {
          setMessages((prev) => [...prev, messageData]) // Ensure this is only called on success
        }

        setUploadedFiles([]) // Clear uploaded files after sending
        setInputValue('') // Clear input after sending

        // Reset the file input reference
        if (fileInputRef.current) {
          fileInputRef.current.value = '' // Reset the file input
        }
        fetchMessages()
      } else {
        toast.error('Failed to send message')
      }
    } catch (error) {
      console.error('Error sending message:', error)
      toast.error('Error sending message')
    }
  }

  // Add initial document message if provided
  useEffect(() => {
    if (message && isOpen) {
      handleSendMessage()
    }
  }, [message, isOpen])

  const formatDate = (dateString: string) => {
    return moment.utc(dateString).utcOffset(7).format('hh:mm A')
  }

  const transformPusherMessage = (pusherMessage) => {
    console.log('Tanggal : ', pusherMessage.createdAt)

    console.log('Pusher Message : ', pusherMessage)

    const createdAt = new Date(pusherMessage.createdAt)

    // Check if the date is valid
    if (isNaN(createdAt.getTime())) {
      return null
    }

    return {
      remark: pusherMessage.remark,
      documentName: pusherMessage.documentName || [], // Assign documentName from pusherMessage
      role: pusherMessage.role,
      createdBy: pusherMessage.createdBy,
      createdAt: createdAt.toISOString(), // Only set if valid
      updatedBy: null,
      updatedAt: null,
      chatIsOpen: pusherMessage.chatIsOpen, // Corrected to use chatIsOpen
      count: 1,
      avatarUrl: pusherMessage.avatarUrl,
    }
  }

  const downloadFile = async (claimNumber: string, documentName: string) => {
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL
      }/api/Claims/GetFile?claimNumber=${claimNumber}&fileName=${encodeURIComponent(documentName)}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    try {
      const response = await axios.get(apiUrl, { headers })

      if (!response.data.isSuccess) {
        throw new Error('Failed to fetch document')
      }

      const data = response.data
      console.log('API Response:', data) // Debug log

      if (data.isSuccess && data.data.url) {
        // Check if URL needs encoding
        let fileUrl = data.data.url

        // Log the URL we're trying to open
        console.log('Attempting to open URL:', fileUrl)

        // Try to decode and re-encode the URL properly
        try {
          const decodedUrl = decodeURIComponent(fileUrl)
          const encodedUrl = encodeURI(decodedUrl)
          console.log('Encoded URL:', encodedUrl)
          window.open(encodedUrl, '_blank')
        } catch (urlError) {
          console.error('Error processing URL:', urlError)
          // Fallback to original URL if encoding fails
          window.open(fileUrl, '_blank')
        }
      } else {
        console.error('API Response Error:', data)
        toast.error('Failed to get document URL')
      }
    } catch (error) {
      console.error('Error fetching document:', error)
      toast.error('Error loading document')
    }
  }

  if (!isOpen) return null

  console.log('Claim Details : ', claimDetails)
  console.log('Claim Details : ', claim)

  return (
    <div className='chat-overlay' data-testid='chat-overlay' onClick={onClose}>
      <div className='chat-container' onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className='chat-header d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center'>
            <div
              className='rounded-circle me-2'
              style={{
                width: '50px',
                height: '50px',
                backgroundColor: '#2E7D32',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              {(claim?.providerName || claimDetails.claimDetails.providerName || '')
                .split(' ')
                .map((name) => name.charAt(0).toUpperCase())
                .join('')}
            </div>
            <div>
              <h5 className='mb-0'>
                {claim?.providerName || claimDetails.claimDetails.providerName}
              </h5>
              <small className='text-muted'>Active</small>
            </div>
          </div>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>

        {/* Chat Body */}
        <div className='chat-body overflow-auto'>
          {messages
            .filter((message) => message.remark !== '')
            .map((message, index) => (
              <div
                key={message.createdAt || index}
                className={`message-row ${message.role === 'PROVIDER' ? 'message-right' : 'message-left'
                  }`}
              >
                {/* Generate avatar from createdBy initials */}
                <div
                  className={`chat-avatar ${message.role === 'PROVIDER' ? 'avatar-right' : 'avatar-left'
                    }`}
                >
                  {
                    message.createdBy
                      ? message.createdBy.includes('@')
                        ? message.createdBy
                          .split('@')[0]
                          .split('.')
                          .map((name) => name.charAt(0).toUpperCase())
                          .join('') // Extract initials from email
                        : message.createdBy
                          .split(' ')
                          .map((name) => name.charAt(0).toUpperCase())
                          .join('') // Handle case if it's a name
                      : '?' // Default to '?' if createdBy is undefined
                  }
                </div>
                <div
                  className={`message-bubble bg-light text-dark ${message.isPending ? 'pending' : ''
                    }`}
                >
                  {message.documentName &&
                    message.documentName.length > 0 &&
                    message.documentName.map((docName, docIndex) => (
                      <div className='document-preview' key={docIndex}>
                        {/* Determine the file type and render the appropriate icon */}
                        {typeof docName === 'string' && docName.endsWith('.pdf') && (
                          <FaFilePdf className='document-icon' />
                        )}
                        {typeof docName === 'string' && docName.endsWith('.xlsx') && (
                          <FaFileExcel className='document-icon' />
                        )}
                        {typeof docName === 'string' && docName.includes('image/') && (
                          <FaFileImage className='document-icon' />
                        )}
                        {typeof docName === 'string' && docName.endsWith('.docx') && (
                          <FaFileAlt className='document-icon' />
                        )}

                        {/* Make the document name clickable to download */}
                        <span
                          onClick={() => downloadFile(claim?.claimNumber || '', docName)}
                          className='document-name'
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: 'blue' }}
                        >
                          {docName}
                        </span>
                      </div>
                    ))}
                  <p className='mb-1'>{message.remark}</p>
                  <small className='text-muted'>
                    {formatDate(message.createdAt)}
                    {message.isPending && <span className='ms-2'>●●●</span>}
                  </small>
                </div>
              </div>
            ))}
          <div ref={messagesEndRef} /> {/* This div will be used to scroll to the latest message */}
        </div>

        {uploadedFiles.length > 0 && (
          <div className='file-preview'>
            {/* <h6>Selected Files:</h6> */}
            <ul>
              {uploadedFiles.map((file, index) => (
                <li key={index} className='file-preview-item'>
                  {/* Dynamic icon based on file type */}
                  {file.type === 'application/pdf' && <FaFilePdf className='file-icon' />}
                  {file.type ===
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && (
                      <FaFileExcel className='file-icon' />
                    )}
                  {file.type.startsWith('image/') && <FaFileImage className='file-icon' />}
                  {file.type ===
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document' && (
                      <FaFileAlt className='file-icon' />
                    )}
                  <span>{file.name}</span>
                  <button
                    onClick={() => handleRemoveFile(file)}
                    style={{
                      border: 'none', // No border
                      borderRadius: '4px', // Optional: rounded corners
                      padding: '4px', // Optional: padding for better click area
                      cursor: 'pointer', // Change cursor to pointer
                    }}
                  >
                    <FaTimes style={{ color: 'red' }} /> {/* Red cross icon */}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Chat Input */}
        <div className='chat-input d-flex'>
          <input
            type='file'
            ref={fileInputRef}
            className='d-none'
            multiple
            accept='.png,.jpg,.jpeg,.pdf'
            onChange={(e) => {
              if (e.target.files) {
                Array.from(e.target.files).forEach((file) => handleFileUpload(file))
              }
            }}
            disabled={isUploading || loading}
          />
          <button
            className='btn btn-secondary me-2'
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || loading}
            style={{ minWidth: '40px' }} // Add fixed minimum width
          >
            <FaPaperclip />
          </button>

          <input
            type='text'
            className='form-control'
            placeholder={isUploading ? 'Uploading file...' : 'Type your message...'}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !loading && !isUploading && handleSendMessage()}
            disabled={loading || isUploading}
          />
          <button
            className='btn btn-primary ms-2'
            onClick={handleSendMessage}
            disabled={loading || isUploading}
            style={{ minWidth: '80px' }} // Add fixed minimum width
          >
            {isUploading ? 'Uploading...' : loading ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChatModal
